'use client';

import { AddressInput, DatePicker, Input, LoadingIndicator } from '@commenda/kakashi';
import { IAddress } from '@commenda/types';
import { Country } from '@prisma/client';
import { useContext, useEffect, useMemo } from 'react';
import { FormContext } from '../../hooks/FormContext';
import { getLocationTestValue } from '../../preview/test-value-registry';
import { isRuleActive } from '../../utils/validation';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';
import {
  useCommendaOSEntitySchema,
  useCommendaOSRead,
} from '@commenda/api/commenda-os-dsl';
import type { DataSourceConfig } from '@commenda/types';
import { STANDARD_CONTEXT_KEYS } from '../../types';
import { DSLContextMarker } from '../../dsl-constants';

// Local interface for form data that allows partial addresses
interface IPartialAddress {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface ILocationFormData {
  address?: IPartialAddress;
  name?: string;
  startDate?: string;
}

/**
 * Convert a full ISO timestamp (e.g., "2024-01-15T00:00:00.000Z") to just the date part ("2024-01-15")
 * The DatePicker component expects ISO8601 date-only format
 */
function toDateOnlyString(dateString: string | undefined | null): string {
  if (!dateString) return '';
  // If it's already a date-only string, return as-is
  if (!dateString.includes('T')) return dateString;
  // Extract the date part before the 'T'
  return dateString.split('T')[0];
}

interface LocationProps {
  fieldId: string;
  label?: string;
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  dataSource?: DataSourceConfig;
  props?: {
    /** The location type (e.g., 'REGISTERED_OFFICE' for corporation, 'CONTACT_ADDRESS' for key person) */
    locationType?: string | null;
    /** Operation mode: 'read' for display only, 'read-write' for editable */
    mode?: 'read' | 'read-write';
    /** Default country for address input */
    defaultCountry?: string;
    /** Whether to show the optional location name field */
    showName?: boolean;
    /** Whether to show the optional start date field */
    showStartDate?: boolean;
    disabled?: boolean;
    /** Entity to read/write location for: 'corporation' (default) or 'keyPerson' */
    entity?: 'corporation' | 'keyPerson';
  };
}

/**
 * Location Component
 *
 * A specialized component for collecting business locations (registered office, operating address, etc.)
 * from Commenda OS. This component:
 *
 * 1. Fetches the location schema to get metadata (location type labels)
 * 2. Uses the location type to read the current address from the corporation
 * 3. Auto-populates label from the schema
 * 4. Uses AddressInput for address collection
 * 5. Supports read-only or read-write modes
 *
 * Configuration:
 * - locationType: The type of location (e.g., 'REGISTERED_OFFICE')
 * - mode: 'read' for display only, 'read-write' for editable
 * - contextBindings: Used to get corporationId from context or previous field
 */
export const LocationComponent = ({
  fieldId,
  label: labelOverride,
  guidance,
  description: descriptionOverride,
  validation,
  dataSource,
  props = {},
}: LocationProps) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
    isPreview,
    registerPreviewFillHandler,
  } = useContext(FormContext);

  const {
    locationType,
    mode = 'read-write',
    defaultCountry = 'US',
    showName = false,
    showStartDate = false,
    disabled: propsDisabled = false,
    entity = 'corporation',
  } = props;

  const contextBindings = dataSource?.commendaOS?.contextBindings;

  // Special marker for form context binding
  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;

  // Valid context keys that the DSL backend accepts
  const VALID_CONTEXT_KEYS: readonly string[] = STANDARD_CONTEXT_KEYS;

  // Build effective context by merging global context with bound field values
  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };

    if (contextBindings) {
      for (const binding of contextBindings) {
        // If using form context marker, the value should already be in dslContext
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
          continue;
        }

        // Skip invalid context keys
        if (!VALID_CONTEXT_KEYS.includes(binding.contextKey)) {
          console.warn(`[Location] Skipping invalid context key: ${binding.contextKey}`);
          continue;
        }

        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          // Convert string values to numbers for ID fields
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }

    // Filter out any invalid keys that might have been in dslContext
    const filteredCtx: typeof ctx = {};
    for (const key of Object.keys(ctx)) {
      if (VALID_CONTEXT_KEYS.includes(key)) {
        filteredCtx[key] = ctx[key];
      }
    }

    return filteredCtx;
  }, [dslContext, formData, contextBindings]);

  // Check if we have context bindings but the bound field doesn't have a value yet
  const hasMissingBoundContext = useMemo(() => {
    if (!contextBindings || contextBindings.length === 0) return false;

    // Filter to only valid context bindings
    const validBindings = contextBindings.filter((binding) =>
      VALID_CONTEXT_KEYS.includes(binding.contextKey)
    );

    if (validBindings.length === 0) return false;

    return validBindings.some((binding) => {
      // If using form context, check if the value exists in dslContext
      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        return (
          dslContext?.[binding.contextKey] === undefined ||
          dslContext?.[binding.contextKey] === null
        );
      }

      const fieldValue = formData[binding.sourceFieldId];
      return fieldValue === undefined || fieldValue === null || fieldValue === '';
    });
  }, [contextBindings, formData, dslContext]);

  // Fetch the location schema to get enum options with metadata
  const { entitySchema, isLoading: schemaLoading } =
    useCommendaOSEntitySchema('location');

  // Find the location type metadata from the schema's locationType filter enum options
  const locationTypeMeta = useMemo(() => {
    if (!entitySchema?.filters?.locationType?.enumOptions || !locationType) {
      return null;
    }

    const options = entitySchema.filters.locationType.enumOptions;
    return options.find((opt) => opt.value === locationType) || null;
  }, [entitySchema, locationType]);

  // Build the read path for fetching the current address
  // Corporation: corporation.locations[locationType=X]
  // Key person: keyPerson.locations (returns array, filtered on frontend)
  const readPath = useMemo(() => {
    if (entity === 'keyPerson') {
      if (!effectiveContext?.directorId) return null;
      return 'keyPerson.locations';
    }
    if (!locationType || !effectiveContext?.corporationId) return null;
    return `corporation.locations[locationType=${locationType}]`;
  }, [entity, locationType, effectiveContext?.corporationId, effectiveContext?.directorId]);

  // Fetch the current location from Commenda OS
  // For keyPerson: returns array of PersonLocation; for corporation: returns a single location object
  const { data: locationReadData, isLoading: valueLoading } = useCommendaOSRead<
    | { address?: IAddress; name?: string; startDate?: string }
    | Array<{ type: string[]; address?: IAddress; isPrimary?: boolean }>
  >(
    readPath,
    effectiveContext as { companyId: number } | undefined,
    { skip: !readPath || hasMissingBoundContext }
  );

  // For keyPerson: find the OPERATING location (corresponds to CONTACT_ADDRESS write type)
  // For corporation: use the single location object directly
  const currentLocation = useMemo((): { address?: IAddress; name?: string; startDate?: string } | null => {
    if (!locationReadData) return null;
    if (entity === 'keyPerson') {
      if (!Array.isArray(locationReadData)) return null;
      const operatingLoc = locationReadData.find(
        (loc) => Array.isArray(loc.type) && loc.type.includes('OPERATING')
      );
      return operatingLoc ? { address: operatingLoc.address } : null;
    }
    return locationReadData as { address?: IAddress; name?: string; startDate?: string };
  }, [entity, locationReadData]);

  // Use the fetched data as initial value, merging with form data
  // Server data takes precedence for initial load, but user edits are preserved
  const locationData = useMemo((): ILocationFormData => {
    const formValue = formData[fieldId] as ILocationFormData | undefined;
    
    // Convert server startDate to date-only format for DatePicker compatibility
    const serverStartDate = toDateOnlyString(currentLocation?.startDate);
    
    // If no form data exists, use server data
    if (!formValue || Object.keys(formValue).length === 0) {
      return {
        address: currentLocation?.address || {},
        name: currentLocation?.name || '',
        startDate: serverStartDate,
      };
    }
    
    // If form data exists but server data has values that form data is missing,
    // merge them (server data fills in missing values)
    return {
      address: formValue.address && Object.keys(formValue.address).length > 0 
        ? formValue.address 
        : (currentLocation?.address || {}),
      name: formValue.name || currentLocation?.name || '',
      startDate: formValue.startDate || serverStartDate,
    };
  }, [formData, fieldId, currentLocation]);

  // Sync fetched data to form when loaded
  // Merge server data with existing form data to fill in missing values
  useEffect(() => {
    if (!currentLocation) return;
    
    const existingData = formData[fieldId] as ILocationFormData | undefined;
    
    // Convert server startDate to date-only format for DatePicker compatibility
    const serverStartDate = toDateOnlyString(currentLocation.startDate);
    
    // If no existing data, set the full server data
    if (!existingData || Object.keys(existingData).length === 0) {
      updateField(fieldId, {
        address: currentLocation.address || {},
        name: currentLocation.name || '',
        startDate: serverStartDate,
      });
      return;
    }
    
    // Check if we need to fill in missing values from server
    const needsUpdate = 
      (!existingData.startDate && serverStartDate) ||
      (!existingData.name && currentLocation.name) ||
      (!existingData.address || Object.keys(existingData.address).length === 0) && currentLocation.address;
    
    if (needsUpdate) {
      updateField(fieldId, {
        address: existingData.address && Object.keys(existingData.address).length > 0 
          ? existingData.address 
          : (currentLocation.address || {}),
        name: existingData.name || currentLocation.name || '',
        startDate: existingData.startDate || serverStartDate,
      });
    }
  }, [currentLocation, fieldId, formData, updateField]);

  // Auto-populate label from schema metadata
  const effectiveLabel = labelOverride || locationTypeMeta?.label || 'Location';
  const effectiveDescription = descriptionOverride;

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const isReadOnly = mode === 'read';
  // For keyPerson, missing directorId means new person — keep editable; only disable for corporation
  const isDisabled =
    propsDisabled ||
    isFieldDisabled(fieldId) ||
    isReadOnly ||
    (hasMissingBoundContext && entity !== 'keyPerson');

  // Preview self-fill: this component owns the location value shape. When the
  // corporation has a real location, the OS sync above already filled it; when
  // it has none, synthesize a test location (in-memory only — preview never
  // writes back to Commenda OS).
  useEffect(() => {
    if (!isPreview || !registerPreviewFillHandler || !fieldId) return undefined;
    registerPreviewFillHandler(fieldId, () => {
      const existingData = formData[fieldId] as ILocationFormData | undefined;
      if (existingData?.name || existingData?.address?.addressLine1) {
        return { kind: 'already-filled' };
      }
      if (schemaLoading || valueLoading) return { kind: 'loading' };
      updateField(fieldId, getLocationTestValue());
      return { kind: 'filled' };
    });
    return () => registerPreviewFillHandler(fieldId, null);
  }, [
    isPreview,
    registerPreviewFillHandler,
    fieldId,
    formData,
    schemaLoading,
    valueLoading,
    updateField,
  ]);

  const handleAddressChange = (addr: IAddress) => {
    const newData = { ...locationData, address: addr };
    updateField(fieldId, newData);
    validateField(fieldId, newData, validation?.rules);
  };

  const handleNameChange = (name: string) => {
    const newData = { ...locationData, name };
    updateField(fieldId, newData);
    validateField(fieldId, newData, validation?.rules);
  };

  const handleStartDateChange = (startDate: string | undefined) => {
    const newData = { ...locationData, startDate: startDate || '' };
    updateField(fieldId, newData);
    validateField(fieldId, newData, validation?.rules);
  };

  // Show loading state
  if (schemaLoading || valueLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  // Show configuration needed state
  if (!locationType) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="p-4 border border-dashed rounded-md bg-bg-secondary">
          <p className="text-sm text-text-tertiary text-center">
            Configure location type in the property editor
          </p>
        </div>
      </div>
    );
  }

  // Show missing context state — but for keyPerson, missing directorId means new person being
  // created (not "no entity selected"), so skip this and render an empty editable form instead.
  if (hasMissingBoundContext && entity !== 'keyPerson') {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <AddressInput
          address={{ addressLine1: '', postalCode: '', city: '', state: '', country: '' }}
          onChange={() => {}}
          defaultCountry={defaultCountry as Country}
          disabled
        />
      </div>
    );
  }

  // Show missing entity context state — only for corporation; for keyPerson, allow editing
  // when directorId is absent (new person being created).
  const missingEntityContext =
    entity === 'keyPerson' ? false : !effectiveContext?.corporationId;
  if (missingEntityContext) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Corporation context required to load location.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(effectiveLabel || effectiveDescription) && (
        <LabelWithGuidanceTooltip
          label={effectiveLabel}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={effectiveDescription}
        />
      )}
      
      {/* Optional Location Name Field */}
      {showName && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Location Name <span className="text-text-tertiary">(optional)</span>
          </label>
          <Input
            value={locationData.name || ''}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., Headquarters, Main Office"
            disabled={isDisabled}
          />
        </div>
      )}

      {/* Optional Start Date Field */}
      {showStartDate && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Start Date <span className="text-text-tertiary">(optional)</span>
          </label>
          <DatePicker
            date={locationData.startDate || undefined}
            setDate={handleStartDateChange}
            placeholder="Select start date"
            disableInput={isDisabled}
          />
        </div>
      )}

      {/* Address Input */}
      <AddressInput
        address={(locationData.address || { addressLine1: '', postalCode: '', city: '', state: '', country: '' }) as IAddress}
        onChange={handleAddressChange}
        defaultCountry={defaultCountry as Country}
        disabled={isDisabled}
      />
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
